import axios from "./index";

class BucketApi {
  // Método base actualizado para soportar proceso_id
  static uploadFile = (file, fileName = "default.jpg", fileType = "general", extraParams = {}) => {
    const formData = new FormData();
    // Si recibimos un FormData, usamos ese directamente
    if (file instanceof FormData) {
      return axios.post(`/bucket/`, file, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    }
    // Si es un archivo normal, construimos el FormData
    formData.append("file", file);
    formData.append("file_name", fileName);
    formData.append("file_type", fileType);
    // Agregar parámetros adicionales si existen
    if (extraParams.proceso_id) {
      formData.append("proceso_id", extraParams.proceso_id);
    }
    if (extraParams.sede_id) {
      formData.append("sede_id", extraParams.sede_id);
    }
    if (extraParams.audiencia_id) {
      formData.append("audiencia_id", extraParams.audiencia_id);
    }
    if (extraParams.plan_pago_id) {
      formData.append("plan_pago_id", extraParams.plan_pago_id);
    }
    const headers = {
      "Content-Type": "multipart/form-data",
    };
    if (file.type === "application/pdf") {
      headers["x-amz-meta-content-type"] = "application/pdf";
      headers["x-amz-meta-content-disposition"] = "inline";
    }
    return axios.post(`/bucket/`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  };

  static checkFileExists = (fileName, fileType = "general", extraParams = {}) => {
    const params = {
      file_name: fileName,
      file_type: fileType,
      check_exists: true,
      ...extraParams,
    };

    return axios.get(`/bucket/`, { params });
  };

  static getFileUrl = (fileName, fileType = "general", extraParams = {}) => {
    const params = {
      file_name: fileName,
      file_type: fileType,
      ...extraParams,
    };

    return axios.get(`/bucket/`, { params });
  };

  static getFileUrlWithCacheBuster = (fileName, fileType = "general", extraParams = {}) => {
    const timestamp = Date.now(); // cache-buster único
    const params = {
      file_name: fileName,
      file_type: fileType,
      ...extraParams,
    };
    return axios.get(`/bucket/`, { params }).then((res) => {
      let url = res.data.file_url;
      // agregamos cache-buster a la URL final
      const separator = url.includes("?") ? "&" : "?";
      url = `${url}${separator}_cb=${timestamp}`;
      return { ...res, data: { ...res.data, file_url: url } };
    });
  };

  static deleteFile = (fileName, fileType = "general", extraParams = {}) => {
    const params = {
      file_name: fileName,
      file_type: fileType,
      ...extraParams,
    };

    return axios.delete(`/bucket/`, { params });
  };

  // Métodos específicos para archivos de Proceso
  static uploadProcesoFile = (file, fileName, procesoId) => {
    return this.uploadFile(file, fileName, "proceso", { proceso_id: procesoId });
  };

  static getProcesoFileUrl = (fileName, procesoId) => {
    return this.getFileUrl(fileName, "proceso", { proceso_id: procesoId });
  };
  static getProcesoFileUrlFresh = (fileName, procesoId) => {
    return this.getFileUrlWithCacheBuster(fileName, "proceso", {
      proceso_id: procesoId,
      force_fresh: true,
    });
  };

  static checkProcesoFileExists = (fileName, procesoId) => {
    return this.checkFileExists(fileName, "proceso", { proceso_id: procesoId });
  };

  static deleteProcesoFile = (fileName, procesoId) => {
    return this.deleteFile(fileName, "proceso", { proceso_id: procesoId });
  };

  // SEDE
  static getSedeFileUrl = (fileName, sedeId) => {
    return this.getFileUrl(fileName, "sede", { sede_id: sedeId });
  };
  static getSedeFileUrlFresh = (fileName, sedeId) => {
    return this.getFileUrlWithCacheBuster(fileName, "sede", {
      sede_id: sedeId,
      force_fresh: true,
    });
  };
  static checkSedeFileExists = (fileName, sedeId) => {
    return this.checkFileExists(fileName, "sede", { sede_id: sedeId });
  };
  static deleteSedeFile = (fileName, sedeId) => {
    return this.deleteFile(fileName, "sede", { sede_id: sedeId });
  };

  // Mantener los métodos existentes para compatibilidad
  static uploadProfileImage = (file) => {
    return this.uploadFile(file, "profile_image.jpg", "profile");
  };

  static getProfileImageUrl = () => {
    return this.getFileUrl("profile_image.jpg", "profile");
  };

  static deleteProfileImage = () => {
    return this.deleteFile("profile_image.jpg", "profile");
  };

  static uploadSignature = (file) => {
    return this.uploadFile(file, "firma_digital.jpg", "signature");
  };

  static getSignatureUrl = () => {
    return this.getFileUrl("firma_digital.jpg", "signature");
  };

  static deleteSignature = () => {
    return this.deleteFile("firma_digital.jpg", "signature");
  };

  static uploadProfessionalCard = (file) => {
    return this.uploadFile(file, "tarjeta_profesional.pdf", "professional_card");
  };

  static getProfessionalCardUrl = () => {
    return this.getFileUrl("tarjeta_profesional.pdf", "professional_card");
  };

  static deleteProfessionalCard = () => {
    return this.deleteFile("tarjeta_profesional.pdf", "professional_card");
  };

  // SOLICITUD
  static checkSolicitudFileExists = (fileName, procesoId) => {
    return this.checkFileExists(fileName, "solicitud", { proceso_id: procesoId });
  };
  static getSolicitudFileUrl = (fileName, procesoId) => {
    return this.getFileUrl(fileName, "solicitud", { proceso_id: procesoId });
  };
  static getSolicitudFileUrlFresh = (fileName, procesoId) => {
    return this.getFileUrlWithCacheBuster(fileName, "solicitud", {
      proceso_id: procesoId,
      force_fresh: true,
    });
  };
  static deleteSolicitudFile = (fileName, procesoId) => {
    return this.deleteFile(fileName, "solicitud", { proceso_id: procesoId });
  };

  // COMUNICACIONES
  static checkComunicacionFileExists = (fileName, procesoId, comunicacionId) => {
    return this.checkFileExists(fileName, "comunicaciones", {
      proceso_id: procesoId,
      comunicacion_id: comunicacionId,
    });
  };
  static getComunicacionFileUrlFresh = (fileName, procesoId, comunicacionId) => {
    return this.getFileUrlWithCacheBuster(fileName, "comunicaciones", {
      proceso_id: procesoId,
      comunicacion_id: comunicacionId,
      force_fresh: true,
    });
  };

  // AUDIENCIAS
  static checkAudienciaFileExists = (fileName, procesoId) => {
    return this.checkFileExists(fileName, "audiencias", { proceso_id: procesoId });
  };
  static getAudienciaFileUrl = (fileName, procesoId) => {
    return this.getFileUrl(fileName, "audiencias", { proceso_id: procesoId });
  };
  // manejo de imágenes con subcarpetas por audiencia y etapa
  static uploadAudienciaImage = (file, fileName, procesoIdentificador, audienciaId) => {
    //const path = `procesos/${procesoIdentificador}/audiencias/${audienciaId}/${fileName}`;
    return this.uploadFile(file, fileName, "audiencias_imagenes", {
      proceso_id: procesoIdentificador,
      audiencia_id: audienciaId,
    });
  };
  static getAudienciaFileUrlFresh = (fileName, procesoIdentificador, audienciaId) => {
    return this.getFileUrlWithCacheBuster(fileName, "audiencias_imagenes", {
      proceso_id: procesoIdentificador,
      audiencia_id: audienciaId,
      force_fresh: true,
    });
  };
  static deleteAudienciaImage = (fileName, procesoIdentificador, audienciaId) => {
    //const path = `procesos/${procesoIdentificador}/audiencias/${audienciaId}/${fileName}`;
    return this.deleteFile(fileName, "audiencias_imagenes", {
      proceso_id: procesoIdentificador,
      audiencia_id: audienciaId,
    });
  };
  static getAudienciaImagesList = (procesoIdentificador, audienciaId) => {
    const carpeta = `procesos/${procesoIdentificador}/audiencias/${audienciaId}/`;
    return axios.get(`/bucket/`, {
      params: {
        file_name: carpeta,
        file_type: "audiencias_imagenes",
        proceso_id: procesoIdentificador,
        audiencia_id: audienciaId,
      },
    });
  };

  static getAllAudienciaImages = (procesoIdentificador, audienciaId) => {
    return axios.get(`/bucket/audiencias/${procesoIdentificador}/${audienciaId}/images/all`, {
      params: {
        file_type: "audiencias_imagenes",
        proceso_id: procesoIdentificador,
        audiencia_id: audienciaId,
        get_all_etapas: true,
      },
    });
  };

  static validateImageFile = (file) => {
    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    const errors = [];
    if (!allowedTypes.includes(file.type)) {
      errors.push("Formato de imagen no permitido. Solo JPG, PNG, GIF o WEBP.");
    }
    if (file.size > 5 * 1024 * 1024) {
      errors.push("La imagen supera el tamaño máximo permitido (5MB).");
    }
    return {
      isValid: errors.length === 0,
      errors,
    };
  };

  static cleanOrphanAudienciaImages = (procesoIdentificador, audienciaId, keepFiles = []) => {
    const payload = {
      proceso_id: procesoIdentificador,
      audiencia_id: audienciaId,
      keep_files: keepFiles,
    };
    console.log("=== ENVIANDO REQUEST DE LIMPIEZA ===");
    console.log("URL:", "/bucket/clean-orphans/");
    console.log("Payload:", JSON.stringify(payload, null, 2));
    console.log("Keep files:", keepFiles);
    return axios
      .post(`/bucket/clean-orphans/`, payload, {
        headers: {
          "Content-Type": "application/json",
        },
      })
      .then((response) => {
        console.log("=== RESPUESTA DE LIMPIEZA ===");
        console.log("Status:", response.status);
        console.log("Data:", response.data);
        return response;
      })
      .catch((error) => {
        console.error("=== ERROR EN LIMPIEZA ===");
        console.error("Error completo:", error);
        console.error("Response data:", error.response?.data);
        console.error("Response status:", error.response?.status);
        throw error;
      });
  };

  static createProcesoZipDownload = async (
    procesoId,
    excludeFiles = [
      "pdf_letra_cambio.pdf",
      "pdf_proyeccion.pdf",
      "pdf_proyeccion_conciliada.pdf",
      "pdf_proyeccion_acuerdo.pdf",
    ]
  ) => {
    const payload = {
      proceso_id: procesoId,
      exclude_files: excludeFiles,
    };
    console.log("=== CREANDO ZIP DE PROCESO ===");
    console.log("URL:", "/bucket/create-proceso-zip/");
    console.log("Payload:", JSON.stringify(payload, null, 2));
    console.log("Proceso ID:", procesoId);
    console.log("Archivos excluidos:", excludeFiles);
    try {
      const response = await axios.post(`/bucket/create-proceso-zip/`, payload, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      console.log("=== RESPUESTA CREACIÓN ZIP ===");
      console.log("Status:", response.status);
      console.log("Data:", response.data);
      return response;
    } catch (error) {
      console.error("=== ERROR CREANDO ZIP ===");
      console.error("Error completo:", error);
      console.error("Response data:", error.response?.data);
      console.error("Response status:", error.response?.status);
      throw error;
    }
  };

  static getProcesoZipDownloadUrl = async (procesoId, zipFilename = null) => {
    const params = {
      proceso_id: procesoId,
    };
    if (zipFilename) {
      params.zip_filename = zipFilename;
    }
    console.log("=== OBTENIENDO URL ZIP EXISTENTE ===");
    console.log("URL:", "/bucket/get-proceso-zip-url/");
    console.log("Params:", params);
    console.log("Proceso ID:", procesoId);
    try {
      const response = await axios.get(`/bucket/get-proceso-zip-url/`, {
        params,
        headers: {
          "Content-Type": "application/json",
        },
      });
      console.log("=== RESPUESTA URL ZIP EXISTENTE ===");
      console.log("Status:", response.status);
      console.log("Data:", response.data);
      return response;
    } catch (error) {
      console.error("=== ERROR OBTENIENDO URL ZIP ===");
      console.error("Error completo:", error);
      console.error("Response data:", error.response?.data);
      console.error("Response status:", error.response?.status);
      throw error;
    }
  };

  static getOrCreateProcesoZip = async (
    procesoId,
    excludeFiles = [
      "pdf_letra_cambio.pdf",
      "pdf_proyeccion.pdf",
      "pdf_proyeccion_conciliada.pdf",
      "pdf_proyeccion_acuerdo.pdf",
    ],
    forceRecreate = false
  ) => {
    console.log("=== INICIANDO GET OR CREATE ZIP ===");
    console.log("Proceso ID:", procesoId);
    console.log("Force recreate:", forceRecreate);
    try {
      let result;
      if (!forceRecreate) {
        // Primero intentar obtener ZIP existente
        console.log("Verificando ZIP existente...");
        try {
          const existingZip = await this.getProcesoZipDownloadUrl(procesoId);
          if (existingZip.data.success) {
            console.log("ZIP existente encontrado");
            return {
              ...existingZip,
              data: {
                ...existingZip.data,
                wasCreated: false,
                message: existingZip.data.message || "ZIP existente encontrado",
              },
            };
          }
        } catch (error) {
          console.log("ZIP no existe, procederemos a crear uno nuevo");
        }
      }
      // Si no existe o se fuerza recrear, crear nuevo ZIP
      console.log("Creando nuevo ZIP...");
      result = await this.createProcesoZipDownload(procesoId, excludeFiles);
      return {
        ...result,
        data: {
          ...result.data,
          wasCreated: true,
        },
      };
    } catch (error) {
      console.error("=== ERROR EN GET OR CREATE ZIP ===");
      console.error("Error completo:", error);
      throw error;
    }
  };

  // COMUNICACIONES - Métodos para manejo de imágenes
  static uploadComunicacionImage = (file, fileName, procesoIdentificador, comunicacionId) => {
    return this.uploadFile(file, fileName, "comunicaciones_imagenes", {
      proceso_id: procesoIdentificador,
      comunicacion_id: comunicacionId,
    });
  };

  static getImagenesComunicacionFileUrlFresh = (fileName, procesoIdentificador, comunicacionId) => {
    return this.getFileUrlWithCacheBuster(fileName, "comunicaciones_imagenes", {
      proceso_id: procesoIdentificador,
      comunicacion_id: comunicacionId,
      force_fresh: true,
    });
  };

  static deleteImagenesComunicacionImage = (fileName, procesoIdentificador, comunicacionId) => {
    return this.deleteFile(fileName, "comunicaciones_imagenes", {
      proceso_id: procesoIdentificador,
      comunicacion_id: comunicacionId,
    });
  };

  static getComunicacionImagesList = (procesoIdentificador, comunicacionId) => {
    return axios.get(`/bucket/list-comunicacion-images/`, {
      params: {
        proceso_id: procesoIdentificador,
        comunicacion_id: comunicacionId,
      },
    });
  };

  static getAllComunicacionImages = (procesoIdentificador, comunicacionId) => {
    return axios.get(
      `/bucket/comunicaciones/${procesoIdentificador}/${comunicacionId}/images/all/`,
      {
        params: {
          file_type: "comunicaciones_imagenes",
          proceso_id: procesoIdentificador,
          comunicacion_id: comunicacionId,
        },
      }
    );
  };

  static cleanOrphanComunicacionImages = (procesoIdentificador, comunicacionId, keepFiles = []) => {
    const payload = {
      proceso_id: procesoIdentificador,
      comunicacion_id: comunicacionId,
      keep_files: keepFiles,
    };

    return axios
      .post(`/bucket/clean-comunicacion-orphans/`, payload, {
        headers: {
          "Content-Type": "application/json",
        },
      })
      .then((response) => {
        console.log("=== RESPUESTA DE LIMPIEZA - COMUNICACIONES ===");
        console.log("Status:", response.status);
        console.log("Data:", response.data);
        return response;
      })
      .catch((error) => {
        console.error("=== ERROR EN LIMPIEZA - COMUNICACIONES ===");
        console.error("Error completo:", error);
        console.error("Response data:", error.response?.data);
        console.error("Response status:", error.response?.status);
        throw error;
      });
  };

  // PLANES PAGO
  static uploadPlanPagoImage = (file, fileName, procesoIdentificador, planPagoId) => {
    return this.uploadFile(file, fileName, "plan_pago", {
      proceso_id: procesoIdentificador,
      plan_pago_id: planPagoId,
    });
  };

  static getPlanPagoFileUrlFresh = (fileName, procesoIdentificador, planPagoId) => {
    return this.getFileUrlWithCacheBuster(fileName, "plan_pago", {
      proceso_id: procesoIdentificador,
      plan_pago_id: planPagoId,
      force_fresh: true,
    });
  };

  static deletePlanPagoImage = (fileName, procesoIdentificador, planPagoId) => {
    return this.deleteFile(fileName, "plan_pago", {
      proceso_id: procesoIdentificador,
      plan_pago_id: planPagoId,
    });
  };

  static getPlanPagoImagesList = (procesoIdentificador, planPagoId) => {
    return axios.get(`/bucket/list-plan-pago-files/`, {
      params: {
        proceso_id: procesoIdentificador,
        plan_pago_id: planPagoId,
      },
    });
  };

  static getAllPlanPagoImages = (procesoIdentificador, planPagoId) => {
    return axios.get(`/bucket/plan_pago/${procesoIdentificador}/${planPagoId}/all/`, {
      params: {
        file_type: "plan_pago",
        proceso_id: procesoIdentificador,
        plan_pago_id: planPagoId,
      },
    });
  };

  // Método helper para redimensionar imágenes (si no existe)
  static resizeImage = (file, maxWidth = 800, maxHeight = 600, quality = 0.8) => {
    return new Promise((resolve) => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = new Image();

      img.onload = () => {
        let { width, height } = img;

        // Calcular nuevas dimensiones manteniendo aspect ratio
        if (width > height) {
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;

        // Dibujar imagen redimensionada
        ctx.drawImage(img, 0, 0, width, height);

        // Convertir a blob
        canvas.toBlob(resolve, file.type, quality);
      };

      img.src = URL.createObjectURL(file);
    });
  };

  // Método helper para convertir blob a base64 (si no existe)
  static convertToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };
}

export default BucketApi;
